import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { HandHeart, Check, X, Mail, Loader2, Send, Clock } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "disabled", label: "Disabled" },
  { value: "rejected", label: "Rejected" },
  { value: "", label: "All statuses" },
];

const HOURS_STATUS_BADGE = {
  pending_review: { label: "Pending", color: "bg-[#FEF3C7] text-[#92400E]" },
  approved: { label: "Approved", color: "bg-[#E8ECE7] text-[#166534]" },
  rejected: { label: "Rejected", color: "bg-[#FEE2E2] text-[#991B1B]" },
};

export default function AdminVolunteers() {
  const [tab, setTab] = useState("queue"); // queue | hours
  return (
    <div className="space-y-6" data-testid="admin-volunteers-page">
      <div>
        <div className="hw-eyebrow mb-2">Community</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Volunteers</h1>
        <p className="text-[#334155] mt-2 max-w-2xl">
          Review volunteer applications, send invites, and approve service hours.
        </p>
      </div>

      <div className="flex gap-2 border-b border-[#E5E5E0]" data-testid="admin-volunteers-tabs">
        <TabBtn label="Volunteer queue" active={tab === "queue"} onClick={() => setTab("queue")} testId="admin-vol-tab-queue" />
        <TabBtn label="Hours review" active={tab === "hours"} onClick={() => setTab("hours")} testId="admin-vol-tab-hours" />
      </div>

      {tab === "queue" ? <VolunteerQueue /> : <HoursReview />}
    </div>
  );
}

const TabBtn = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
      active ? "text-[#1E3A8A] border-[#1E3A8A]" : "text-[#334155] border-transparent hover:text-[#1E3A8A]"
    }`}
  >
    {label}
  </button>
);

function VolunteerQueue() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/admin/volunteers", { params })
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (v) => {
    try {
      await api.post(`/admin/volunteers/${v.id}/approve`);
      toast.success(`Approved · ${v.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const reject = async (v) => {
    const reason = window.prompt(`Reject ${v.name}?\nOptional reason (visible in audit log):`);
    if (reason === null) return;
    try {
      await api.post(`/admin/volunteers/${v.id}/reject`, { reason: reason.trim() || null });
      toast.success(`Declined · ${v.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#334155]">Status:</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="admin-volunteers-status-filter"
            className="px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-sm"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setShowInvite(true)}
          data-testid="admin-volunteers-invite-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
        >
          <Send className="w-3.5 h-3.5" /> Invite a volunteer
        </button>
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSent={() => { setShowInvite(false); load(); }} />}

      {loading ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="admin-volunteers-empty">
          <HandHeart className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No volunteers match this filter.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-volunteers-list">
          {rows.map((v) => (
            <li
              key={v.id}
              data-testid={`admin-volunteer-${v.id}`}
              className="bg-white border border-[#E5E5E0] rounded-2xl p-5"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-[#0F172A]">{v.name}</span>
                    <StatusPill status={v.status} />
                    <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] bg-[#E0E7FF] px-2 py-0.5 rounded-full">
                      {v.source === "admin_invite" ? "Invited" : "Self-signup"}
                    </span>
                  </div>
                  <div className="text-xs text-[#334155]">
                    {v.email}{v.phone && ` · ${v.phone}`}
                    {v.counties?.length > 0 && ` · ${v.counties.join(", ")}`}
                  </div>
                  {v.skills && <p className="text-sm text-[#334155] mt-2"><strong>Skills:</strong> {v.skills}</p>}
                  {v.why_volunteer && <p className="text-sm text-[#334155] mt-1"><strong>Why:</strong> {v.why_volunteer}</p>}
                </div>
                {v.status === "pending" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => reject(v)}
                      data-testid={`admin-volunteer-reject-${v.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[#991B1B] hover:bg-[#FEE2E2] inline-flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Decline
                    </button>
                    <button
                      type="button"
                      onClick={() => approve(v)}
                      data-testid={`admin-volunteer-approve-${v.id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] inline-flex items-center gap-1"
                    >
                      <Check className="w-3 h-3" /> Approve
                    </button>
                  </div>
                )}
                {(v.status === "active" || v.status === "disabled") && (
                  <a
                    href="/users#volunteer-accounts"
                    onClick={(e) => { e.preventDefault(); window.location.href = "/users#volunteer-accounts"; }}
                    data-testid={`admin-volunteer-manage-${v.id}`}
                    className="text-[11px] px-3 py-1.5 rounded-full bg-[#F9F8F6] border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#E8ECE7] inline-flex items-center gap-1 flex-shrink-0"
                  >
                    Manage account &rarr;
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const StatusPill = ({ status }) => {
  const tone = {
    pending: "bg-[#FEF3C7] text-[#92400E]",
    active: "bg-[#E8ECE7] text-[#166534]",
    rejected: "bg-[#FEE2E2] text-[#991B1B]",
    invited: "bg-[#DBEAFE] text-[#1E40AF]",
    inactive: "bg-[#F1F5F9] text-[#475569]",
    disabled: "bg-[#FEE2E2] text-[#991B1B]",
  }[status] || "bg-[#F1F5F9] text-[#475569]";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${tone}`}>
      {status}
    </span>
  );
};

function InviteModal({ onClose, onSent }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/volunteers/invite", form);
      toast.success(`Invite emailed to ${form.email}`);
      onSent();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0F172A]/40 z-50 flex items-center justify-center p-4" data-testid="admin-volunteers-invite-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl p-6 max-w-md w-full space-y-4">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-[#1E3A8A]" />
          <h3 className="font-display text-lg font-semibold">Invite a volunteer</h3>
        </div>
        <p className="text-sm text-[#334155]">
          We&apos;ll email them a magic link to set their password and activate the account.
        </p>
        <input type="text" required placeholder="Full name" value={form.name} onChange={set("name")}
          data-testid="admin-volunteers-invite-name"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
        <input type="email" required placeholder="Email" value={form.email} onChange={set("email")}
          data-testid="admin-volunteers-invite-email"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
        <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")}
          data-testid="admin-volunteers-invite-phone"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
        <textarea rows={2} placeholder="Personal note (optional)" value={form.message} onChange={set("message")}
          data-testid="admin-volunteers-invite-message"
          className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
        {error && <div className="px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose}
            data-testid="admin-volunteers-invite-cancel"
            className="px-4 py-2 rounded-full border border-[#E5E5E0] text-[#334155] text-sm hover:bg-[#F9F8F6]">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="admin-volunteers-invite-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Send invite
          </button>
        </div>
      </form>
    </div>
  );
}

function HoursReview() {
  const [status, setStatus] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = status ? { status } : {};
    api.get("/admin/volunteer-hours", { params })
      .then(({ data }) => setRows(data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    try {
      await api.post(`/admin/volunteer-hours/${r.id}/approve`);
      toast.success(`Approved · ${r.volunteer_name || "volunteer"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const reject = async (r) => {
    const notes = window.prompt("What needs to change? (notes visible to the volunteer)", "");
    if (notes === null) return;
    if (!notes.trim()) { toast.error("Please provide a short note"); return; }
    try {
      await api.post(`/admin/volunteer-hours/${r.id}/reject`, { reviewer_notes: notes.trim() });
      toast.success(`Returned · ${r.volunteer_name || "volunteer"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-sm text-[#334155]">Status:</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="admin-hours-status-filter"
          className="px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-sm"
        >
          <option value="pending_review">Pending review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl h-32 animate-pulse" />
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="admin-hours-empty">
          <Clock className="w-12 h-12 mx-auto text-[#1E3A8A] opacity-50 mb-3" strokeWidth={1.5} />
          <p className="text-[#334155]">No hours entries match this filter.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="admin-hours-list">
          {rows.map((r) => {
            const badge = HOURS_STATUS_BADGE[r.status] || HOURS_STATUS_BADGE.pending_review;
            return (
              <li key={r.id} data-testid={`admin-hours-row-${r.id}`} className="bg-white border border-[#E5E5E0] rounded-2xl px-5 py-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[#0F172A]">{r.volunteer_name || "Volunteer"}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.label}
                      </span>
                    </div>
                    <div className="text-xs text-[#334155] mt-0.5">
                      {r.event_title || r.activity_label || "Free-form activity"} · {r.activity_date} · <strong>{r.hours.toFixed(2)}h</strong>
                    </div>
                    {r.notes && <p className="text-sm text-[#334155] mt-1">{r.notes}</p>}
                  </div>
                  {r.status === "pending_review" && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => reject(r)}
                        data-testid={`admin-hours-reject-${r.id}`}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#E5E5E0] text-[#991B1B] hover:bg-[#FEE2E2]">Return</button>
                      <button onClick={() => approve(r)}
                        data-testid={`admin-hours-approve-${r.id}`}
                        className="text-xs px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white hover:bg-[#172554]">Approve</button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
